import { featureCollection, point } from "@turf/helpers";
import { destination } from "@turf/turf";
import { useContext, useEffect, useId } from "react";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { MapitStateContext } from "src/states/MapitState";

export function SquareSelectorMap(
   props: {
      active: boolean,
      value?: SquareSelection,
      onChange?: (val: SquareSelection) => void
   }
) {
   const { state } = useContext(MapitStateContext);
   const uniqueID = useId()

   useEffect(() => {
      if (props.active) {
         let map = state.map?.getMapPop();
         let currVal = props.value ?? findSquareFromMap(map)
         if (!currVal) {
            return;
         }
         map.addSource(uniqueID, {
            type: "geojson",
            data: squareToMapGeom(currVal)
         });
         map.addLayer({
            id: uniqueID + "-outline",
            type: "line",
            source: uniqueID,
            paint: {
               "line-color": "#fff",
               "line-width": 4,
            }
         });
         map.addLayer({
            id: uniqueID,
            type: "line",
            source: uniqueID,
            paint: {
               "line-color": "#c25700",
               "line-width": 2,
               "line-dasharray": [4, 1, 2, 1]
            }
         });
         map.addLayer({
            id: uniqueID + "-handles",
            type: "circle",
            source: uniqueID,
            paint: {
               "circle-radius": 6,
               "circle-color": "#ff7300",
               "circle-stroke-color": "#fff",
               "circle-stroke-width": 2
            },
            filter: ["==", ["geometry-type"], "Point"]
         });

         const activateMapInteractions = (e) => {
            MouseEnter(e, uniqueID + "-handles", uniqueID, currVal, (val) => {
               console.log("new val", val);
               props.onChange?.(val);
            });
         };
         map.on("mouseenter", uniqueID + "-handles", activateMapInteractions);
         return () => {
            map.off("mouseenter", uniqueID + "-handles", activateMapInteractions);
            if (map.getLayer(uniqueID + "-outline")) {
               map.removeLayer(uniqueID + "-outline");
            }
            if (map.getLayer(uniqueID)) {
               map.removeLayer(uniqueID);
            }
            if (map.getLayer(uniqueID + "-handles")) {
               map.removeLayer(uniqueID + "-handles");
            }
            if (map.getSource(uniqueID)) {
               map.removeSource(uniqueID);
            }
         }
      }
   }, [props.active, props.value]);
   return <></>
}

function onMapMove(action: "move" | "vertical" | "horizontal" | ""): (square: SquareSelection, start: llCoords, end: llCoords) => SquareSelection {
   console.log("action", action);
   switch (action) {
      case "move":
         return moveSquare;
      case "horizontal":
         return resizeHorizontal;
      case "vertical":
         return resizeVertical;
      default:
         return (square: SquareSelection, _start: llCoords, _end: llCoords) => {
            return square;
         }
   }
}

function bestBoxAction(features: GeoJSON.Feature[]): "move" | "vertical" | "horizontal" | "" {
   let x = features.map(f => f.properties?.type)?.filter((a) => a);
   if (x.includes("center")) {
      return "move";
   }
   if (x.includes("vertical")) {
      console.log("vertical");
      return "vertical";
   }
   if (x.includes("horizontal")) {
      console.log("horizontal");
      return "horizontal";
   }
   return ""
}

function moveSquare(square: SquareSelection, start: llCoords, end: llCoords): SquareSelection {
   let deltaX = end.lng - start.lng;
   let deltaY = end.lat - start.lat;
   return { ...square, x: square.x + deltaX, y: square.y + deltaY };
}

function resizeVertical(square: SquareSelection, start: llCoords, end: llCoords): SquareSelection {
   return resizeSquare(square, { lng: square.x, lat: start.lat }, { lng: square.x, lat: end.lat });
}

function resizeHorizontal(square: SquareSelection, start: llCoords, end: llCoords): SquareSelection {
   return resizeSquare(square, { lng: start.lng, lat: square.y }, { lng: end.lng, lat: square.y });
}

function resizeSquare(square: SquareSelection, start: llCoords, end: llCoords): SquareSelection {
   let startUtm = GenerateGeom.convertFromLatLngToUTM(start);
   let dirW = start.lng > square.x ? 1 : -1;
   let dirH = start.lat > square.y ? 1 : -1;

   let endUtm = GenerateGeom.convertFromLatLngToUTM(end);
   let deltaW = ((endUtm.x - startUtm.x) << 1) * dirW;
   let deltaH = ((endUtm.y - startUtm.y) << 1) * dirH;
   if (start.lat === square.y) {
      deltaH = 0;
   } else if (start.lng === square.x) {
      deltaW = 0;
   }
   return { ...square, w: square.w + deltaW, h: square.h + deltaH };
}

export function findSquareFromMap(map: maplibregl.Map): SquareSelection | null {
   let ne = map.getBounds().getNorthEast();
   let sw = map.getBounds().getSouthWest();
   let utmNe = GenerateGeom.convertFromLatLngToUTM(ne);
   let utmSw = GenerateGeom.convertFromLatLngToUTM(sw);
   let centerX = (utmNe.x + utmSw.x) / 2;
   let centerY = (utmNe.y + utmSw.y) / 2;
   let width = Math.abs(utmNe.x - utmSw.x);
   let height = Math.abs(utmNe.y - utmSw.y);
   let size = Math.min(width, height) * 0.40;
   let { lng, lat } = GenerateGeom.convertFromUTMtoLatLng({ x: centerX, y: centerY });
   return { x: lng, y: lat, w: size, h: size };
}

export function squareToMapGeom(square: SquareSelection | null) {
   if (!square) return featureCollection([]);
   let center = { lng: square.x, lat: square.y };
   let center_point = point([center.lng, center.lat]);
   let e = destination(center_point, square.w / 2000, 90);
   let n = destination(center_point, square.h / 2000, 0);
   let ne = { lng: e.geometry.coordinates[0], lat: n.geometry.coordinates[1] };
   let sw = { lng: 2 * center.lng - ne.lng, lat: 2 * center.lat - ne.lat };

   return featureCollection<any>([
      bboxSquare(sw, ne),
      // centerPoint(ne),
      centerPoint(center),
      ...edgeDots(sw, ne)
   ]);
}

function bboxSquare(sw: llCoords, ne: llCoords): GeoJSON.Feature<GeoJSON.Polygon> {
   return {
      type: "Feature",
      geometry: {
         type: "Polygon",
         coordinates: [[
            [sw.lng, ne.lat],
            [ne.lng, ne.lat],
            [ne.lng, sw.lat],
            [sw.lng, sw.lat],
            [sw.lng, ne.lat]
         ]],
      },
      properties: {}
   }
}
function centerPoint(center: llCoords): GeoJSON.Feature<GeoJSON.Point> {
   return {
      type: "Feature",
      geometry: {
         type: "Point",
         coordinates: [center.lng, center.lat]
      },
      properties: {
         type: "center"
      }
   }
}

function edgeDots(sw: llCoords, ne: llCoords): GeoJSON.Feature<GeoJSON.Point>[] {
   let line = [
      [sw.lng, ne.lat],
      [ne.lng, ne.lat],
      [ne.lng, sw.lat],
      [sw.lng, sw.lat],
      [sw.lng, ne.lat]
   ];

   return line.map((coord, index) => {
      if (index === 0) {
         return null;
      }
      return midPoint({ lng: coord[0], lat: coord[1] }, { lng: line[index - 1][0], lat: line[index - 1][1] }, index % 2 === 1 ? "vertical" : "horizontal");
   }).filter((f) => f !== null) as GeoJSON.Feature<GeoJSON.Point>[];
}

function midPoint(p1: llCoords, p2: llCoords, type: "vertical" | "horizontal"): GeoJSON.Feature<GeoJSON.Point> {
   return {
      type: "Feature",
      geometry: {
         type: "Point",
         coordinates: [(p1.lng + p2.lng) / 2, (p1.lat + p2.lat) / 2]
      },
      properties: {
         type
      }
   }
}

/**
 * x: center longitude
 * y: center latitude
 * w: width in meters
 * h: height in meters
 */
export type SquareSelection = {
   x: number, y: number, w: number, h: number
}

type llCoords = {
   lng: number, lat: number
}


// MARK: Map interactions
function MouseEnter(e, layer: string, source: string, square: SquareSelection | null, onFinish: (newSquare: SquareSelection) => void) {
   if (!square) {
      console.log("No square to edit");
      return;
   };
   var action: undefined | ((square, start, end) => SquareSelection) = undefined;
   var start: undefined | llCoords = undefined;

   e.target.getCanvas().style.cursor = "move";

   const mouseUp = (e) => {
      if (start && action) {
         onFinish(action?.(square, start, e.lngLat));
      }
      e.target.off("mouseup", mouseUp);
      e.target.off("mousemove", mouseMove)
   }

   const mouseMove = (e) => {
      let cur = e.lngLat;
      if (start && action) {
         if (e.target.getSource(source)) {
            let newSquare = action?.(square, start, cur);
            (e.target.getSource(source) as maplibregl.GeoJSONSource).setData(squareToMapGeom(newSquare));
         }
      }
   }

   const mouseDown = (e) => {
      let featues = e.features;
      let actionType = bestBoxAction(featues);
      action = onMapMove(actionType);
      start = e.lngLat;
      e.preventDefault();
      e.target.on("mouseup", mouseUp);
      e.target.on("mousemove", mouseMove)
   }

   //LAST cleaning
   const mouseLeave = () => {
      e.target.getCanvas().style.cursor = ""
      e.target.off("mouseleave", layer, mouseLeave);
      e.target.off("mousedown", layer, mouseDown);
   }

   e.target.on("mousedown", layer, mouseDown);
   e.target.on("mouseleave", layer, mouseLeave);
}